import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';

const Container = styled.div`
  padding: 2rem;
  max-width: 1100px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const BackLink = styled(Link)`
  color: #005eb8;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #f2f2f2;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const Label = styled.label`
  font-weight: bold;
`;

const PriceInput = styled.input`
  width: 90px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const PercentInput = styled.input`
  width: 70px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Button = styled.button`
  padding: 8px 16px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover { background-color: #004a94; }
  &:disabled { background-color: #99b9d6; cursor: not-allowed; }
`;

const CategoryTitle = styled.h3`
  margin: 2rem 0 0.75rem;
  color: #333;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td { padding: 10px 12px; border-bottom: 1px solid #eee; text-align: left; vertical-align: middle; }
  th { background-color: #f9f9f9; font-size: 0.9rem; color: #555; }
`;

const FinalPrice = styled.span`
  font-weight: bold;
  color: #005eb8;
`;

const ServicePricing = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [edits, setEdits] = useState({}); // { [id]: { price, discountPercent } }
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  const token = localStorage.getItem('adminToken');
  const api = useMemo(() => axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    if (!token) navigate('/');
  }, [token, navigate]);

  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admins/services');
      setServices(res.data.services);
      const nextEdits = {};
      res.data.services.forEach((s) => {
        nextEdits[s.id] = { price: String(s.price), discountPercent: String(s.discountPercent || 0) };
      });
      setEdits(nextEdits);
    } catch (err) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleEditChange = (id, field, value) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const handleSave = async (id) => {
    const edit = edits[id];
    const price = parseFloat(edit.price);
    const discountPercent = parseFloat(edit.discountPercent) || 0;

    if (Number.isNaN(price) || price < 0) {
      toast.error('Enter a valid price.');
      return;
    }
    if (discountPercent < 0 || discountPercent > 100) {
      toast.error('Discount must be between 0 and 100.');
      return;
    }

    setSavingId(id);
    try {
      const res = await api.put(`/admins/services/${id}`, { price, discountPercent });
      setServices((prev) => prev.map((s) => (s.id === id ? res.data.service : s)));
      toast.success('Service updated.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update service.');
    } finally {
      setSavingId(null);
    }
  };

  const handleBulkApply = async () => {
    const price = parseFloat(bulkPrice);
    if (Number.isNaN(price) || price < 0) {
      toast.error('Enter a valid price.');
      return;
    }
    if (!window.confirm(`Set ALL ${services.length} services to £${price.toFixed(2)}? This cannot be undone.`)) return;

    setBulkSaving(true);
    try {
      await api.put('/admins/services/bulk-price', { price });
      toast.success('All service prices updated.');
      await loadServices();
      setBulkPrice('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update all prices.');
    } finally {
      setBulkSaving(false);
    }
  };

  const categories = useMemo(() => {
    const grouped = {};
    services.forEach((s) => {
      if (!grouped[s.category]) grouped[s.category] = [];
      grouped[s.category].push(s);
    });
    return grouped;
  }, [services]);

  if (loading) {
    return <Container><p>Loading services...</p></Container>;
  }

  return (
    <Container>
      <Header>
        <h1>Service Pricing</h1>
        <BackLink to="/dashboard">&larr; Back to Dashboard</BackLink>
      </Header>

      <BulkBar>
        <Label htmlFor="bulk-price">Set ALL services to:</Label>
        <span>£</span>
        <PriceInput
          id="bulk-price"
          type="number"
          min="0"
          step="0.01"
          value={bulkPrice}
          onChange={(e) => setBulkPrice(e.target.value)}
          placeholder="0.00"
        />
        <Button onClick={handleBulkApply} disabled={bulkSaving || !bulkPrice}>
          {bulkSaving ? 'Applying...' : 'Apply to All'}
        </Button>
      </BulkBar>

      {Object.entries(categories).map(([category, categoryServices]) => (
        <div key={category}>
          <CategoryTitle>{category}</CategoryTitle>
          <Table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Price (£)</th>
                <th>Discount (%)</th>
                <th>Customer Pays</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {categoryServices.map((service) => {
                const edit = edits[service.id] || { price: '0', discountPercent: '0' };
                const previewPrice = parseFloat(edit.price) || 0;
                const previewDiscount = parseFloat(edit.discountPercent) || 0;
                const finalPrice = previewDiscount > 0
                  ? (previewPrice * (1 - previewDiscount / 100))
                  : previewPrice;

                return (
                  <tr key={service.id}>
                    <td>{service.name}</td>
                    <td>
                      <PriceInput
                        type="number"
                        min="0"
                        step="0.01"
                        value={edit.price}
                        onChange={(e) => handleEditChange(service.id, 'price', e.target.value)}
                      />
                    </td>
                    <td>
                      <PercentInput
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={edit.discountPercent}
                        onChange={(e) => handleEditChange(service.id, 'discountPercent', e.target.value)}
                      />
                    </td>
                    <td>
                      {previewDiscount > 0 ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '0.5rem' }}>
                            £{previewPrice.toFixed(2)}
                          </span>
                          <FinalPrice>£{finalPrice.toFixed(2)}</FinalPrice>
                        </>
                      ) : (
                        <FinalPrice>£{finalPrice.toFixed(2)}</FinalPrice>
                      )}
                    </td>
                    <td>
                      <Button onClick={() => handleSave(service.id)} disabled={savingId === service.id}>
                        {savingId === service.id ? 'Saving...' : 'Save'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ))}
    </Container>
  );
};

export default ServicePricing;
